import type { FieldLayout, FieldProps } from "@repo/types";
import { DynamicFormikField } from "./DynamicFormikField";
import { DynamicFormikCollapsibleSection } from "./DynamicFormikCollapsibleSection";
import { evaluateCondition } from "./conditionEvaluator";
import * as yup from "yup";
import type { FormikErrors } from "formik";

export interface DynamicFormikFieldsWrapperProps {
  fields: FieldProps[];
  fieldsMap?: { [name: string]: FieldProps };
  layout: FieldLayout;
  errors?: FormikErrors<unknown>;
  values: {
    [x: string]: unknown;
  };
  disabled?: boolean;
}

export const DynamicFormikFieldsWrapper = ({
  fields,
  fieldsMap,
  layout,
  errors,
  values,
  disabled,
}: DynamicFormikFieldsWrapperProps) => {
  fieldsMap ??= fields.reduce(
    (map, field) => ((map[field.name] = field), map),
    Object.create(null)
  );

  if (layout) {
    if (layout.condition) {
      // if layout has a condition, compute it
      if (!evaluateCondition(layout.condition, values)) return null;
    }
    let jsx = (
      <>
        {layout.children.map((item, ind) => {
          if (typeof item === "string") {
            const { name, type, ...props } = fieldsMap![item] ?? {};
            if (!name) {
              console.warn(`Field '${item}' missing from field list`);
              return null;
            }

            return (
              <DynamicFormikField
                key={name}
                errors={errors}
                name={name}
                type={type}
                value={values[name]}
                values={values}
                {...props}
                disabled={disabled}
              />
            );
          } else {
            return (
              <DynamicFormikFieldsWrapper
                key={ind}
                errors={errors}
                fields={fields}
                fieldsMap={fieldsMap}
                layout={item}
                values={values}
                disabled={disabled}
              />
            );
          }
        })}
      </>
    );
    if (layout.collapsible) {
      // if layout section is collapsible, wrap in collapser
      jsx = (
        <DynamicFormikCollapsibleSection
          title={layout.sectionTitle}
          defaultCollapsed={layout.defaultCollapsed}
        >
          {jsx}
        </DynamicFormikCollapsibleSection>
      );
    }
    return jsx;
  }

  return (
    <div className="flex flex-col gap-y-4">
      {fields.map(({ name, type, ...props }) => (
        <DynamicFormikField
          key={name}
          errors={errors}
          name={name}
          type={type}
          value={values[name]}
          values={values}
          disabled={disabled}
          {...props}
        />
      ))}
    </div>
  );
};

DynamicFormikFieldsWrapper.validationFields = (fields: FieldProps[]) => {
  const schema = Object.create(null);

  for (const field of fields) {
    if (field.required)
      schema[field.name] = yup
        .mixed()
        .required(
          typeof field.required === "string"
            ? field.required
            : "This field is required"
        );
  }

  return schema;
};
